import React, { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Check, X } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

/**
 * Customer-facing landing page for quote Accept / Decline links.
 *
 * Email links land here with `?token=…&action=accept|decline`. We **never
 * auto-submit** the action on page load — email scanners (Outlook Safe Links,
 * Proofpoint, Apple Mail Privacy Protection, etc.) routinely pre-fetch every
 * URL in an email and would burn through the response before the real
 * customer ever clicks. We show one big confirm button instead; scanners
 * don't click buttons, but humans do.
 */
export default function CustomerQuoteResponse() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const action = params.get("action");

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [alreadyResponded, setAlreadyResponded] = useState(false);

  const submit = async (a) => {
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post(`/quotes/${id}/customer-response`, { token, action: a });
      setDone(r.data.status);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404 || status === 403) {
        setAlreadyResponded(true);
      } else {
        setError(e.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Friendly "already responded" landing ────────────────────────────────
  if (alreadyResponded) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="cust-resp-already">
          <div className="w-14 h-14 mx-auto flex items-center justify-center mb-6" style={{ background: "var(--ab-surface-2)", color: "var(--ab-text-muted)" }}>
            <Check size={28} />
          </div>
          <h1 className="font-display text-4xl font-black mb-3">Already responded</h1>
          <p className="text-sm mb-8" style={{ color: "var(--ab-text-muted)" }}>
            This quote has already been responded to or is no longer available. If you have questions,
            reach out to the shop directly and we'll be happy to help.
          </p>
          <button className="btn-secondary" onClick={() => navigate("/")} data-testid="cust-resp-already-home">Return Home</button>
        </div>
      </Layout>
    );
  }

  // ── Success state after POST ────────────────────────────────────────────
  if (done) {
    const accepted = done === "approved" || done === "accepted";
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="cust-resp-done">
          <div className="w-14 h-14 mx-auto flex items-center justify-center mb-6" style={{ background: accepted ? "var(--ab-primary)" : "var(--ab-danger)", color: "var(--ab-bg)" }}>
            {accepted ? <Check size={28} /> : <X size={28} />}
          </div>
          <h1 className="font-display text-4xl font-black mb-3">Quote {accepted ? "ACCEPTED" : "DECLINED"}</h1>
          <p className="text-sm" style={{ color: "var(--ab-text-muted)" }}>
            {accepted
              ? "Thanks! The shop has been notified — payment will be issued via e-transfer shortly."
              : "Thanks for letting us know. The quote has been removed."}
          </p>
          <button className="btn-secondary mt-8" onClick={() => navigate("/")}>Return Home</button>
        </div>
      </Layout>
    );
  }

  // ── Confirmation prompt — always requires a real click ──────────────────
  if (action === "accept" || action === "decline") {
    const isAccept = action === "accept";
    const heading = isAccept ? "Accept this quote?" : "Decline this quote?";
    const sub = isAccept
      ? "Tap the button below to confirm. We'll let the shop know right away."
      : "Tap the button below to confirm. The quote will be removed from the shop's queue.";
    const cta = isAccept ? "Confirm Accept" : "Confirm Decline";
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>AB Microplay</div>
          <h1 className="font-display text-4xl font-black mb-3" data-testid="cust-resp-heading">{heading}</h1>
          <p className="text-sm mb-10" style={{ color: "var(--ab-text-muted)" }}>{sub}</p>
          <button
            onClick={() => submit(action)}
            disabled={submitting || !token}
            className={isAccept ? "btn-primary" : "btn-danger"}
            style={{ padding: "18px 32px", fontSize: 16, minWidth: 220 }}
            data-testid={isAccept ? "cust-resp-confirm-accept" : "cust-resp-confirm-decline"}
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : cta}
          </button>
          {!token && (
            <div className="mt-6 text-xs" style={{ color: "var(--ab-danger)" }}>
              This link is missing a security token — please re-open the original email.
            </div>
          )}
          {error && (
            <div className="mt-6 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="cust-resp-error">
              {error}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ── Fallback for someone visiting the URL without ?action= ──────────────
  return (
    <Layout>
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-black mb-3">Missing action</h1>
        <p className="text-sm" style={{ color: "var(--ab-text-muted)" }}>
          Please re-open the original quote email and tap Accept or Decline.
        </p>
      </div>
    </Layout>
  );
}
